(() => {
  if (customElements.get('blog-load-more')) {
    return;
  }

  class BlogLoadMore extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.loadMoreButton = this.querySelector('[data-load-more-button]');
      this.articlesGrid = this.querySelector('[data-articles-grid]');
      this.sectionId = this.dataset.sectionId;
      this.nextPageUrl = this.dataset.nextPageUrl || null;
    }

    connectedCallback() {
      if (!this.loadMoreButton || !this.articlesGrid) {
        return;
      }

      this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this));
      this.updateButtonVisibility();
    }

    handleLoadMore(event) {
      event.preventDefault();

      if (this.isLoading || !this.nextPageUrl) {
        return;
      }

      this.isLoading = true;
      this.setLoadingState(true);

      // Build the section rendering API URL
      const url = this.buildSectionUrl(this.nextPageUrl);

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          this.handleResponse(html);
        })
        .catch((error) => {
          console.error('Error loading more articles:', error);
          this.setLoadingState(false);
          this.isLoading = false;
        });
    }

    buildSectionUrl(nextPageUrl) {
      // Extract the page parameter from the next page URL
      const url = new URL(nextPageUrl, window.location.origin);
      const page = url.searchParams.get('page') || '2';

      // Build section rendering API URL
      const sectionUrl = new URL(window.location.pathname, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.sectionId);
      sectionUrl.searchParams.set('page', page);

      return sectionUrl.toString();
    }

    handleResponse(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the blog-load-more component or articles grid in the response
      const responseComponent = doc.querySelector('blog-load-more');
      const responseGrid = responseComponent
        ? responseComponent.querySelector('[data-articles-grid]')
        : doc.querySelector('[data-articles-grid]');
      const responseButton = responseComponent
        ? responseComponent.querySelector('[data-load-more-button]')
        : doc.querySelector('[data-load-more-button]');

      if (!responseGrid) {
        this.hideButton();
        this.setLoadingState(false);
        this.isLoading = false;
        return;
      }

      // Extract article cards from the response
      const articles = responseGrid.querySelectorAll('.card-article');

      if (articles.length === 0) {
        this.hideButton();
        this.setLoadingState(false);
        this.isLoading = false;
        return;
      }

      // Append articles to the existing grid
      articles.forEach((article) => {
        this.articlesGrid.appendChild(article.cloneNode(true));
      });

      // Update next page URL from the response
      let newNextPageUrl = null;
      if (responseButton && responseButton.dataset.nextPageUrl) {
        newNextPageUrl = responseButton.dataset.nextPageUrl;
      } else if (responseComponent && responseComponent.dataset.nextPageUrl) {
        newNextPageUrl = responseComponent.dataset.nextPageUrl;
      }

      this.nextPageUrl = newNextPageUrl;

      // Update button data attribute
      if (this.loadMoreButton) {
        if (newNextPageUrl) {
          this.loadMoreButton.dataset.nextPageUrl = newNextPageUrl;
        } else {
          delete this.loadMoreButton.dataset.nextPageUrl;
        }
      }

      // Update current page
      this.currentPage++;

      this.updateButtonVisibility();
      this.setLoadingState(false);
      this.isLoading = false;

      // Trigger lazy loading for new images if needed
      if (window.lazySizes) {
        window.lazySizes.init();
      }
    }

    setLoadingState(loading) {
      if (!this.loadMoreButton) {
        return;
      }

      if (loading) {
        this.loadMoreButton.disabled = true;
        this.loadMoreButton.setAttribute('aria-busy', 'true');
        const originalText = this.loadMoreButton.dataset.originalText || this.loadMoreButton.textContent.trim();
        this.loadMoreButton.dataset.originalText = originalText;
        this.loadMoreButton.textContent = this.loadMoreButton.dataset.loadingText || 'Loading...';
      } else {
        this.loadMoreButton.disabled = false;
        this.loadMoreButton.removeAttribute('aria-busy');
        const originalText = this.loadMoreButton.dataset.originalText;
        if (originalText) {
          this.loadMoreButton.textContent = originalText;
        }
      }
    }

    updateButtonVisibility() {
      if (!this.loadMoreButton) {
        return;
      }

      if (!this.nextPageUrl) {
        this.hideButton();
      } else {
        this.showButton();
      }
    }

    hideButton() {
      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = 'none';
      }
    }

    showButton() {
      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = '';
      }
    }
  }

  customElements.define('blog-load-more', BlogLoadMore);
})();

